import { createFileRoute, redirect } from '@tanstack/react-router';
import confetti from 'canvas-confetti';
import { ExternalLink } from 'lucide-react';

import { BillingInfoCard } from '@/components/features/billing/billing-info-card';
import { CurrentPlanBadge } from '@/components/features/billing/current-plan-badge';
import { Plans } from '@/components/features/billing/types';
import {
  useCurrentOrganization,
  useCurrentPlan,
} from '@/components/features/membership/membership-provider';
import { PricingPlans } from '@/components/features/organization/pricing-plans';
import { Can } from '@/components/features/permission/can';
import { Button } from '@/components/ui/button';
import {
  Page,
  PageContent,
  PageHeader,
  PageHeaderAction,
  PageHeaderDescription,
  PageHeaderInfo,
  PageHeaderTitle,
} from '@/components/ui/layout/page';
import { createPortalSessionMutation } from '@/mutations/billing/createPortalSessionMutation';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

const billingRedirectParamsSchema = z.object({
  status: z.enum(['success', 'cancelled']).optional(),
  plan: z.nativeEnum(Plans).optional(),
});

export const Route = createFileRoute('/_authenticated/organization/billing')({
  component: RouteComponent,
  validateSearch: billingRedirectParamsSchema,
  beforeLoad: async ({ search, context }) => {
    const { t } = context;
    if (search.status === 'success') {
      setTimeout(() => {
        confetti({
          spread: 360,
          ticks: 50,
          gravity: 0,
          decay: 0.94,
          startVelocity: 30,
          colors: ['#FFE400', '#FFBD00', '#E89400', '#FFCA6C', '#FDFFB8'],
          particleCount: 100,
          scalar: 1.2,
          shapes: ['star'],
        });
      }, 1000);

      toast.success(
        t('authenticated.organization.billing.toast.success', {
          plan: search.plan,
        })
      );

      throw redirect({
        // eslint-disable-next-line
        to: location.pathname,
        search: {},
        replace: true,
      });
    }
  },
});

function RouteComponent() {
  const { organizationId } = useCurrentOrganization();
  const currentPlan = useCurrentPlan();
  const { t } = useTranslation();

  const { mutate: createPortalSession, isPending: isPortalSessionPending } =
    useMutation({
      ...createPortalSessionMutation({
        organizationId,
        returnUrl: window.location.href,
      }),
      onSuccess: (response) => {
        if (response.isOk()) {
          const { url } = response.value;

          window.location.href = url;
        }
      },
    });

  return (
    <Page>
      <PageHeader>
        <PageHeaderInfo>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <PageHeaderTitle>
                {t('authenticated.organization.billing.title')}
              </PageHeaderTitle>
            </div>
            <CurrentPlanBadge />
          </div>

          <PageHeaderDescription>
            {t('authenticated.organization.billing.description')}
          </PageHeaderDescription>
        </PageHeaderInfo>

        <Can permissions={['billing:update']}>
          <PageHeaderAction>
            {currentPlan !== Plans.FREE && (
              <Button
                variant="outline"
                disabled={isPortalSessionPending}
                onClick={() => createPortalSession()}
                icon={<ExternalLink />}
                isLoading={isPortalSessionPending}
              >
                {t('authenticated.organization.billing.manageSubscription')}
              </Button>
            )}
          </PageHeaderAction>
        </Can>
      </PageHeader>

      <PageContent>
        <div className="space-y-8">
          <Can permissions={['billing:update']}>
            <BillingInfoCard organizationId={organizationId} />
          </Can>
          <PricingPlans />
        </div>
      </PageContent>
    </Page>
  );
}
